import type { CatalogFetchResult, CatalogManifest } from './types'

import { appHttp } from '@/shared/http'

/**
 * Transport for the versioned catalog mirror. Catalog reads own their degraded UX (never a
 * toast) and must accept 304 as success, so every call carries its own `validateStatus`.
 *
 * `/catalog/*` accepts no query parameters — the payload is always complete.
 */
export class CatalogClient {
  constructor(private readonly app: Pick<typeof appHttp, 'get'> = appHttp) {}

  private config(etag?: string | null) {
    return {
      suppressGlobalErrorHandler: true,
      meta: { suppressGlobalErrorHandler: true },
      validateStatus: (status: number) => (status >= 200 && status < 300) || status === 304,
      ...(etag ? { headers: { 'If-None-Match': etag } } : {}),
    }
  }

  private async request<T>(url: string, etag?: string | null): Promise<CatalogFetchResult<T>> {
    const response = await this.app.get<T>(url, this.config(etag))
    if (response.status === 304) return { status: 304 }
    const responseEtag = (response.headers as Record<string, unknown>)['etag']
    return {
      status: 200,
      etag: typeof responseEtag === 'string' ? responseEtag : null,
      body: response.data,
    }
  }

  fetchManifest(etag?: string | null): Promise<CatalogFetchResult<CatalogManifest>> {
    return this.request<CatalogManifest>('/catalog/manifest/', etag)
  }

  /** `url` always comes from the manifest entry — body paths are never hardcoded. */
  fetchEntry<T>(url: string, etag?: string | null): Promise<CatalogFetchResult<T>> {
    return this.request<T>(url, etag)
  }
}

export const catalogClient = new CatalogClient()
